import {
  BatchTooLargeError,
  ConcurrentModificationError,
  ConflictError,
  JOB_SCOPED_COLLECTIONS,
  RECORD_SCOPED_COLLECTIONS,
  USER_SCOPED_COLLECTIONS,
  MAX_BATCH_WRITES,
  NotFoundError,
  type BatchWrite,
  type CollectionName,
  type DataPort,
  type Scope,
} from '@buildmate/core';
import { MemoryRepository } from './memory-repository.ts';

const JOB_SCOPED = new Set<CollectionName>(JOB_SCOPED_COLLECTIONS);
const USER_SCOPED = new Set<CollectionName>(USER_SCOPED_COLLECTIONS);
const RECORD_SCOPED = new Set<CollectionName>(RECORD_SCOPED_COLLECTIONS);

/** Scope is part of the key, so a cross-tenant read is impossible by construction. */
function keyFor(scope: Scope, name: CollectionName): string {
  // Matches the Firebase adapter, which cannot build a path without the job.
  // Defaulting to a shared store instead would make every job's holidays
  // visible to every other job here and nowhere else — the kind of divergence
  // that passes every test and fails in production.
  if (JOB_SCOPED.has(name) && !scope.jobId) {
    throw new TypeError(`Collection "${name}" is job-scoped but no jobId was supplied`);
  }
  // Same reasoning for the recipient: sharing one store across users would
  // make every notification visible to everybody here and nowhere else.
  if (USER_SCOPED.has(name) && !scope.userId) {
    throw new TypeError(`Collection "${name}" is user-scoped but no userId was supplied`);
  }
  // And for the record a per-record collection hangs beneath (P5-14): one
  // store shared across change orders would show every change order's log
  // on every change order.
  //
  // The **parent's** own scoping decides whether a jobId is wanted, which
  // mirrors `collectionPath` in the Firebase adapter. The first three
  // record-scoped collections all hung beneath a job-scoped record, so this
  // demanded a jobId outright; `safetyAcknowledgements` hangs beneath a
  // company-level notice and has none.
  if (RECORD_SCOPED.has(name)) {
    if (!scope.record) {
      throw new TypeError(`Collection "${name}" hangs beneath a record, but none was supplied`);
    }
    if (JOB_SCOPED.has(scope.record.collection as CollectionName) && !scope.jobId) {
      throw new TypeError(
        `Collection "${name}" hangs beneath "${scope.record.collection}", which is job-scoped, but no jobId was supplied`,
      );
    }
  }
  return [
    scope.companyId,
    scope.jobId ?? '-',
    scope.userId ?? '-',
    scope.record ? `${scope.record.collection}:${scope.record.id}` : '-',
    name,
  ].join('/');
}

export class MemoryDataPort implements DataPort {
  private readonly repos = new Map<string, MemoryRepository<{ id: string }>>();

  collection<T extends { id: string }>(scope: Scope, name: CollectionName) {
    const key = keyFor(scope, name);
    let repo = this.repos.get(key);
    if (!repo) {
      repo = new MemoryRepository<{ id: string }>(key);
      this.repos.set(key, repo);
    }
    return repo as unknown as MemoryRepository<T>;
  }

  /**
   * Apply every write or none.
   *
   * Validated in full before anything is applied, then applied. There is no
   * partial state to roll back from, which is the same guarantee Firestore
   * gives inside a transaction — a fake that applied half a batch would make
   * every unit test above it meaningless.
   */
  async commit(writes: readonly BatchWrite[]): Promise<void> {
    if (writes.length > MAX_BATCH_WRITES) throw new BatchTooLargeError(writes.length);

    const seen = new Set<string>();
    for (const write of writes) {
      const id = write.kind === 'create' ? write.entity.id : write.id;
      const target = `${keyFor(write.scope, write.collection)}/${id}`;
      if (seen.has(target)) {
        throw new TypeError(`A batch cannot write the same document twice: ${target}`);
      }
      seen.add(target);
    }

    for (const write of writes) {
      const repo = this.collection<{ id: string; updatedAt?: string }>(
        write.scope,
        write.collection,
      );
      if (write.kind === 'create') {
        if (await repo.get(write.entity.id)) {
          throw new ConflictError(write.collection, write.entity.id);
        }
        continue;
      }

      const existing = await repo.get(write.id);
      if (write.kind === 'update' && !existing) {
        throw new NotFoundError(write.collection, write.id);
      }
      // A guarded write applies only if the document still says what the
      // caller read. Checked here, before anything is applied, so a stale
      // write takes the whole batch down rather than half-applying it.
      if (write.expect && existing && existing.updatedAt !== write.expect.updatedAt) {
        throw new ConcurrentModificationError(write.collection, write.id);
      }
    }

    for (const write of writes) {
      const repo = this.collection(write.scope, write.collection);
      if (write.kind === 'create') await repo.create(write.entity);
      else if (write.kind === 'update') await repo.update(write.id, write.patch);
      else await repo.delete(write.id);
    }
  }

  reset(): void {
    this.repos.clear();
  }
}
